import { useRecoilState, useResetRecoilState } from "recoil";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  STUDIO_QUERY,
  MUTATE_TOGGLE_CREATORCARD,
} from "../../GraphQL/studioFacet";
import { SINGLE_CREATOR_CARD } from "../../GraphQL/singleCreatorCard";
import { CREATOR_CARD_CLIPS } from "../../GraphQL/creatorCardClips";
import { BASE_REQUEST } from "../../GraphQL/clipFeed";
import { TFacetQueryType, ERoute } from "../../@types";
import { StudioSession } from "../../State/studioSession";
import { URLS } from "../../Utilities/Maps";
import { useUser } from "../../Hooks/useUser";
import { useMutateFollowers } from "../../Hooks/useMutateFollowers";
import { CLEAR_MY_ENABLED_CARDS } from "../../GraphQL/clearMyEnabledCards";
import { useIsMobileApp } from "../../Hooks/useIsMobileApp";
import { GET_MY_SIGNATURE } from "../../GraphQL/loadoutSelector";
import { SignatureSelectorState } from "../../State/loadoutSelector";
import { AllstarModalState } from "../../State/modals";

import {
  GET_MOMENT,
  MOMENT_CREATE_CLIP,
} from "MVVM/GraphQL/mutations/momentRequestClip";

export const StudioViewModel = (route: ERoute, query: TFacetQueryType) => {
  const { cardId, momentId }: any = useParams();
  const { allstarUser, logout, getFreshProfile } = useUser();

  const isRemix = route === "remix";

  const [data, setData] = useState<any>({
    facets: null,
    count: null,
    hits: [],
    userActivated: [],
    homepage: true,
    loading: true,
  });

  const [singleCardData, setSingleCardData] = useState<any>({
    data: null,
    id: cardId,
    loading: true,
  });

  const [remixData, setRemixData] = useState<any>({
    moment: false,
    remixes: false,
  });

  const [cardClips, setCardClips] = useState<any>({
    clips: [],
    loading: true,
  });

  const { isMobileApp } = useIsMobileApp();
  const [studioSession, setStudioSession] = useRecoilState(StudioSession);
  const resetSession = useResetRecoilState(StudioSession);
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const [, setSkippedForRemix] = useState<boolean>(false);
  const { mutateFollowers } = useMutateFollowers();
  const [, setAllstarModalState] = useRecoilState(AllstarModalState);
  const [signatureSession, setSignatureSession] = useRecoilState(
    SignatureSelectorState,
  );

  useQuery(GET_MY_SIGNATURE, {
    skip: !allstarUser?.user?._id,
    variables: {
      user: allstarUser?.user?._id,
      signature: true,
    },

    onCompleted: ({ getLoadouts }) => {
      const genreClipCards = getLoadouts?.clipCards?.filter(
        (card: { card: { effect: string } }) =>
          card.card.effect === "FACET_LOOKUP",
      );
      const selectedGenresCards = genreClipCards?.map(
        (card: { card: { _id: string } }) => card.card,
      );
      if (getLoadouts) {
        setSignatureSession({
          ...signatureSession,
          mySignature: getLoadouts,
          hasASignature: true,
          selectedGenres: selectedGenresCards,
        });
      }
    },
  });

  // GraphQl Queries
  const [facetRefetch, { loading: facetLoading }] = useLazyQuery(STUDIO_QUERY, {
    onCompleted: (data) => {
      const facets = data?.facets?.facets;
      const hits = data?.facets?.cards;
      const count = data?.facets?.total;
      const homepage = data?.facets?.homepage;
      const homepageData = data?.facets?.homepageData;
      const userCards = data?.facets?.userCards;
      const shouldPreselectCards =
        route === "remix" &&
        allstarUser.loggedIn &&
        !studioSession.preselectedCardsHasPopulated;
      setData({
        facets,
        count,
        hits,
        homepage,
        homepageData,
        loading: false,
      });

      if (route === "studio" && isFirstTime) {
        setIsFirstTime(false);
        setStudioSession({
          ...studioSession,
          userActivated: userCards,
        });
      }
      if (route === "remix") {
        const updated: any = { ...studioSession };

        if (shouldPreselectCards) {
          updated.preselectedCardsHasPopulated = true;
        }

        setStudioSession({
          ...updated,
          hits: hits,
        });
      }
    },
    onError: (err) => {
      /*
      @JOHN
      Need to handle token expiry or token resets here...
      */

      // logout("token expiry");
      console.log(`facetRefetch error! ${err.message}`);
    },

    notifyOnNetworkStatusChange: true,
  });
  const [toggleCardState] = useMutation(MUTATE_TOGGLE_CREATORCARD, {
    onError: (err) => {
      /* 
      @JOHN
      Need to handle token expiry or token resets here...
      */
      console.log(`toggleCardState error! ${err.message}`);
    },
    refetchQueries: [{ query: STUDIO_QUERY }],
  });
  const [clearMyEnabledCards] = useMutation(CLEAR_MY_ENABLED_CARDS, {
    onError: (err: any) => {
      /* 
      @JOHN
      Need to handle token expiry or token resets here...
      */
      console.log(`clearMyEnabledCards error! ${err.message}`);
    },
    refetchQueries: [{ query: STUDIO_QUERY }],
  });

  const [requestRemixedClipsFeed] = useLazyQuery(BASE_REQUEST);
  const [requestSingleCreatorCard] = useLazyQuery(SINGLE_CREATOR_CARD);
  const [requestCreatorCardClips] = useLazyQuery(CREATOR_CARD_CLIPS);
  const [momentCreateClip] = useMutation(MOMENT_CREATE_CLIP, {
    onError: (err: any) => {},
  });

  const getCreatorCardClips = async (cardId: string) => {
    setCardClips({ clips: [], loading: true });
    const clips = await requestCreatorCardClips({
      variables: { cardId: cardId },
    });
    const creatorCardClips = clips?.data?.creatorCardClips ?? [];
    setCardClips({ clips: creatorCardClips, loading: false });
    return;
  };
  const postRemix = async () => {
    setStudioSession({ ...studioSession, requestingAPI: true });
    const moment = remixData?.moment;
    const matchId = moment?.match?.id;

    await momentCreateClip({
      variables: {
        momentCreateClipInput: {
          ids: [moment.id],
          matchId,
          effectsOverrides: {
            applySignatureCards: studioSession.useSignature,
            creatorCards: studioSession.userActivated.map(
              (card: any) => card._id,
            ),
          },
        },
      },
    });

    setStudioSession({
      ...studioSession,
      requestingAPI: false,
      remixQueued: true,
    });
    return;
  };
  const [getMoment] = useLazyQuery(GET_MOMENT);

  const populateRequiredData = async () => {
    if (cardId) {
      try {
        const data = await requestSingleCreatorCard({
          variables: { cardId: cardId },
        });
        setSingleCardData({ data: data.data.creatorCard, loading: false });

        await getCreatorCardClips(cardId);
        return;
      } catch (e: any) {
        window.location.replace(URLS.STUDIO);
      }
    }

    if (route === "remix") {
      setSkippedForRemix(true);

      const momentData = await getMoment({
        variables: {
          id: momentId,
        },
      });

      const moment = momentData?.data?.moment;

      setStudioSession((prev: any) => {
        return {
          ...prev,
          isRemixSession: true,
        };
      });

      facetRefetch({
        variables: {
          option2: true,
          ...query,
        },
      });

      setRemixData({
        ...remixData,
        moment: moment,
      });
    }
  };

  useEffect(() => {
    if (!isRemix) {
      facetRefetch({
        variables: {
          option2: true,
          getUserCards: isFirstTime && !isRemix,
          ...query,
        },
      });
    }
  }, [isRemix]);

  useEffect(() => {
    populateRequiredData();

    return () => {
      resetSession();
    };
  }, [route]); //eslint-disable-line

  useEffect(() => {
    if (signatureSession.mySignature) {
      setStudioSession((prev: any) => {
        return {
          ...prev,
          useSignature: signatureSession?.mySignature?.active,
        };
      });
    }
  }, [signatureSession.mySignature, setStudioSession]);

  return {
    allstarUser,
    getFreshProfile,
    logout,
    toggleCardState,
    remixData,
    requestSingleCreatorCard,
    getCreatorCardClips,
    requestRemixedClipsFeed,
    facetRefetch,
    facetLoading,
    singleCardData,
    setSingleCardData,
    cardsData: data,
    cardClips: cardClips,
    setData: setData,
    postRemix,
    mutateFollowers,
    clearMyEnabledCards,
    isMobileApp,
  };
};

export const validSearch = [
  "type",
  "game",
  "theme",
  "mechanic",
  "behaviour",
  "series",
  "proOnly",
  "myCards",
];

export const queryToUrl = async (query: any, route: string) => {
  let queryString = "";
  const queryKeys = Object.keys(query);

  // Add the query for every value in the object:
  const arrayOfParam = [];
  for (const key of queryKeys) {
    if (!query[key]) continue;
    if (key === "myCards") continue;
    arrayOfParam.push(`${key}=${JSON.stringify(query[key])}`);
  }
  if (arrayOfParam.length) {
    // setDisplayNew(false);
    // setHasQuery(true);

    queryString = `?${arrayOfParam.join("&")}`;
  } else {
    console.log("NO QUERY");
    // setDisplayNew(true);
    // setHasQuery(false);
  }

  // add to window:
  window.history.replaceState(null, "null", `${route}${queryString}`);
};
